import {
  PricingLineItem,
  WindowsCurrency,
} from '@cutr/constants/windows/windows';
import { getCustomerPricingSummary } from '@cutr/constants/windows/windows-pricing';
import { useFlag } from '@unleash/proxy-client-react';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAgentLoggedIn } from '@/api/login';
import Card from '@/blocks/Card';
import { onFocusSelect } from '@/hooks';
import { Button } from '@/primitives/Button';
import { Download, Icon } from '@/primitives/Icons';
import { saveBlob } from '@/utils/file';
import { currencyFormatter } from '@/utils/format';

import { useWindowStore } from '../api/windowStore';
import { usePositionImages } from '../interfaces/renderer/useStageRefsMap';
import { Disclosure } from '../primitives/Disclosure';
import { OrderLoaderType } from '../queries/order/loaders';
import { windowApi } from '../queries/windowApi';
import styles from './CustomerPriceBlock.module.css';
import { PricingLineItems } from './OrderSummary';

export const CustomerPriceBlock = ({
  order,
  readonly = false,
}: {
  order: OrderLoaderType;
  readonly?: boolean;
}) => {
  const { t } = useTranslation();
  const { setCustomerMarkupPercentage, setCustomerTaxPercentage } =
    useWindowStore();
  const exportImages = usePositionImages(order.positions);
  const isAgent = useAgentLoggedIn();
  const isDebugPricingEnabled = useFlag('ctr-12165-debug-window-pricing');

  const [preparingPdf, setPreparingPdf] = React.useState(false);

  const currency = 'eur';

  const totalAmountExclVAT = order.pricingLineItems.reduce(
    (acc, item) => acc + (item.totalAmount || 0),
    0
  );

  const [markupPercentage, setMarkupPercentage] = React.useState(
    order.customerMarkupPercentage
  );
  const [taxPercentage, setTaxPercentage] = React.useState(
    order.customerTaxPercentage
  );

  const { customerTotalExVAT, positionLineItems, orderLineItems } =
    getCustomerPricingSummary({
      ...order,
      customerMarkupPercentage: markupPercentage,
      customerTaxPercentage: taxPercentage,
    });

  const customerTotal = customerTotalExVAT * (1 + taxPercentage / 100);

  const handleDownloadPDF = async () => {
    if (!order.id) return;

    const base64Images = exportImages();

    try {
      setPreparingPdf(true);

      const { data, filename } = await windowApi.getOrderPDF(
        order.id,
        base64Images
      );
      saveBlob(data, filename);
    } catch (error) {
      toast.error(t('agent.toasts.fileDownloadError'));
    } finally {
      setPreparingPdf(false);
    }
  };

  const handleMarkupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value >= 0 && value <= 100) {
      setMarkupPercentage(value);
      setCustomerMarkupPercentage(value);
    }
  };

  const handleTaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value >= 0 && value <= 100) {
      setTaxPercentage(value);
      setCustomerTaxPercentage(value);
    }
  };
  return (
    <div className={styles.container}>
      <Card style={{ padding: 'var(--space-m)' }}>
        <div>
          <h3>{t('reseller.customerPrice.title')}</h3>
        </div>

        {isDebugPricingEnabled && (
          <DetailedCustomerPricing
            positionLineItems={positionLineItems}
            orderLineItems={orderLineItems}
            currency={order.currency}
          />
        )}

        <ul className={cn(styles.total, styles.exVat)}>
          <li className={styles.bolder}>
            <strong>{t('reseller.quoteSummary.totalExcludingVAT')}</strong>
            <strong>
              {currencyFormatter(totalAmountExclVAT / 100, currency)}
            </strong>
          </li>
        </ul>

        <div className={styles.percentageRow}>
          <strong>{t('reseller.customerPrice.markup')}</strong>
          <div className={styles.percentageInput}>
            {!readonly ? (
              <input
                className={styles.percentageInputField}
                type="number"
                value={markupPercentage}
                onFocus={onFocusSelect}
                onChange={handleMarkupChange}
              />
            ) : (
              <span>{markupPercentage}</span>
            )}
            <strong>%</strong>
          </div>
        </div>

        <ul className={cn(styles.total, styles.exVat)}>
          <li className={styles.bolder}>
            <strong>{t('reseller.customerPrice.customerTotalExVat')}</strong>
            <strong>
              {currencyFormatter(customerTotalExVAT / 100, currency)}
            </strong>
          </li>
        </ul>

        <div className={styles.percentageRow}>
          <strong>{t('reseller.customerPrice.stateTax')}</strong>
          <div className={styles.percentageInput}>
            {!readonly ? (
              <input
                className={styles.percentageInputField}
                type="number"
                min={0}
                value={taxPercentage}
                onFocus={onFocusSelect}
                onChange={handleTaxChange}
              />
            ) : (
              <span>{taxPercentage}</span>
            )}
            <strong>%</strong>
          </div>
        </div>

        <ul className={styles.total}>
          <li className={styles.bolder}>
            <strong>{t('reseller.customerPrice.customerTotal')}</strong>
            <strong>{currencyFormatter(customerTotal / 100, currency)}</strong>
          </li>
        </ul>

        <Button
          className="full"
          icon={<Icon icon={<Download />} />}
          variant="secondary"
          onClick={handleDownloadPDF}
          disabled={preparingPdf}
        >
          {preparingPdf
            ? t('reseller.customerPrice.preparingPdf')
            : t('reseller.customerPrice.downloadPdf')}
        </Button>

        {!isAgent && <LogoUploadHint />}
      </Card>
    </div>
  );
};

const DetailedCustomerPricing = ({
  positionLineItems,
  orderLineItems,
  currency,
}: {
  positionLineItems: PricingLineItem[];
  orderLineItems: PricingLineItem[];
  currency: WindowsCurrency;
}) => {
  const allLineItems = [...orderLineItems, ...positionLineItems];

  if (allLineItems.length === 0) {
    return null;
  }

  return (
    <Disclosure
      title={'Detailed customer pricing'}
      level={5}
      style={{ marginTop: '1rem', paddingBottom: '1rem' }}
    >
      <PricingLineItems pricingLineItems={allLineItems} currency={currency} />
    </Disclosure>
  );
};

const LogoUploadHint = () => {
  const { t } = useTranslation();
  const [hasLogo, setHasLogo] = React.useState<boolean>(false);

  React.useEffect(() => {
    windowApi.getOrderSettings().then((settings) => {
      setHasLogo(!!settings.logoUrl);
    });
  }, []);

  if (hasLogo) {
    return null;
  }

  return (
    <div className={styles.logoUploadHint}>
      <a href="/windows/settings">
        <p>
          {t('reseller.customerPrice.logoUploadHint')}{' '}
          <span className={styles.arrow}>→</span>
        </p>
      </a>
    </div>
  );
};
